// Pine Script → JavaScript transpiler
// Produces code compatible with the M16 Web Worker indicator runtime.

import type { BinaryOp, CallExpr, Expr, IfStmt, Program, Stmt } from '../ast/index'
import { TA_BUILTINS } from '../builtins/ta'

export interface TranspileResult {
  /** Generated JavaScript string ready for the M16 worker */
  code: string
  /** Errors found during transpilation */
  errors: string[]
  /** Metadata extracted from indicator() declaration */
  meta: {
    title: string
    overlay: boolean
  }
}

// ── Series-type tracker ───────────────────────────────────────────────────────
// Variables known to hold arrays (series). Used to emit correct operators.

const BUILTIN_SERIES = new Set([
  'open',
  'high',
  'low',
  'close',
  'volume',
  'time',
  'timestamp',
  'hl2',
  'hlc3',
  'ohlc4',
])

// ── Default plot colors ───────────────────────────────────────────────────────

const PLOT_COLORS = [
  '#2962ff',
  '#26a69a',
  '#ef5350',
  '#ffeb3b',
  '#ff9800',
  '#ab47bc',
  '#e91e63',
  '#00bcd4',
]

// ── Transpiler ────────────────────────────────────────────────────────────────

class Transpiler {
  private lines: string[] = []
  private errors: string[] = []
  private seriesVars = new Set(BUILTIN_SERIES)
  private plotIndex = 0
  private meta = { title: 'Indicator', overlay: false }

  transpile(program: Program): TranspileResult {
    // Built-in computed series (hl2 etc.)
    this.emit(
      'const hl2 = close.map((_,i)=>high[i]!==null&&low[i]!==null?(high[i]+low[i])/2:null);'
    )
    this.emit(
      'const hlc3 = close.map((_,i)=>high[i]!==null&&low[i]!==null?(high[i]+low[i]+close[i])/3:null);'
    )
    this.emit(
      'const ohlc4 = close.map((_,i)=>high[i]!==null&&low[i]!==null?(open[i]+high[i]+low[i]+close[i])/4:null);'
    )
    this.emit('const bar_index = close.map((_,i)=>i);')
    this.emit('const nz = (v,def=0) => v!==null&&v!==undefined?v:def;')
    this.emit('const math = Math;')
    this.emit('')

    for (const stmt of program.stmts) {
      this.transpileStmt(stmt)
    }

    return {
      code: this.lines.join('\n'),
      errors: this.errors,
      meta: this.meta,
    }
  }

  private emit(line: string) {
    this.lines.push(line)
  }

  private error(msg: string) {
    this.errors.push(msg)
  }

  // ── Statements ─────────────────────────────────────────────────────────────

  private transpileStmt(stmt: Stmt) {
    switch (stmt.kind) {
      case 'VersionDecl':
        return // ignore
      case 'IndicatorDecl': {
        const d = stmt
        this.meta.title = d.title
        this.meta.overlay = d.overlay
        return
      }
      case 'VarAssign': {
        const s = stmt
        const rhs = this.transpileExpr(s.value)
        // Track as series if RHS is series
        if (this.isSeries(s.value)) this.seriesVars.add(s.name)
        this.emit(`const ${jsIdent(s.name)} = ${rhs};`)
        return
      }
      case 'TupleAssign': {
        const s = stmt
        const rhs = this.transpileExpr(s.value)
        const tmpVar = `_tuple_${this.plotIndex++}`
        this.emit(`const ${tmpVar} = ${rhs};`)
        // Determine tuple keys from TA builtins
        const keys = this.getTupleKeys(s.value)
        for (let i = 0; i < s.names.length; i++) {
          const name = s.names[i]
          const key = keys[i]
          if (key) {
            this.emit(`const ${jsIdent(name)} = ${tmpVar}.${key};`)
          } else {
            this.emit(`const ${jsIdent(name)} = ${tmpVar}[${i}] ?? null;`)
          }
          this.seriesVars.add(name)
        }
        return
      }
      case 'ExprStmt': {
        const s = stmt
        const code = this.transpileExprStmt(s.expr)
        if (code) this.emit(code)
        return
      }
      case 'IfStmt': {
        this.transpileIfStmt(stmt)
        return
      }
    }
  }

  private transpileIfStmt(stmt: IfStmt) {
    // Pine if inside a statement context is complex; emit as comment warning
    const cond = this.transpileExpr(stmt.condition)
    this.emit(`// if (${cond}) — multi-line if blocks are limited in this transpiler`)
    for (const s of stmt.consequent) this.transpileStmt(s)
    if (stmt.alternate) {
      this.emit('// else')
      for (const s of stmt.alternate) this.transpileStmt(s)
    }
  }

  /** Returns null for non-side-effectful expressions we skip */
  private transpileExprStmt(expr: Expr): string | null {
    if (expr.kind !== 'CallExpr') return null
    const call = expr
    const name = call.callee.join('.')

    // plot / plotLine
    if (name === 'plot' || name === 'plotLine') {
      return this.emitPlot(call, 'line')
    }
    // plotHistogram
    if (name === 'plotHistogram') {
      return this.emitPlot(call, 'histogram')
    }
    // plotshape / plotchar — treat as plot
    if (name === 'plotshape' || name === 'plotchar') {
      return this.emitPlot(call, 'line')
    }
    // hline — emit as horizontal reference value (ignored in current runtime)
    if (name === 'hline') {
      return `// hline(${call.args.map((a) => this.transpileExpr(a)).join(', ')})`
    }
    // bgcolor / barcolor / fill — visual, not supported
    if (name === 'bgcolor' || name === 'barcolor' || name === 'fill') {
      return `// ${name}(...) — not supported`
    }
    // indicator() handled as IndicatorDecl already
    if (name === 'indicator') return null

    return null
  }

  private emitPlot(call: CallExpr, type: 'line' | 'histogram'): string {
    const color = PLOT_COLORS[this.plotIndex % PLOT_COLORS.length]
    const titleArg = call.kwargs.find((k) => k.key === 'title')
    const colorArg = call.kwargs.find((k) => k.key === 'color')
    const widthArg = call.kwargs.find((k) => k.key === 'linewidth' || k.key === 'lineWidth')

    const label =
      titleArg?.value.kind === 'StringLit'
        ? (titleArg.value as { kind: 'StringLit'; value: string }).value
        : `Plot ${this.plotIndex + 1}`

    const plotColor =
      colorArg?.value.kind === 'StringLit'
        ? (colorArg.value as { kind: 'StringLit'; value: string }).value
        : color

    const lineWidth = widthArg ? this.transpileExpr(widthArg.value) : '1'

    const seriesExpr = call.args[0] ? this.transpileExpr(call.args[0]) : 'null'

    const fnName = type === 'histogram' ? 'plotHistogram' : 'plot'
    this.plotIndex++
    return `${fnName}(${seriesExpr}, { label: ${JSON.stringify(label)}, color: ${JSON.stringify(plotColor)}, lineWidth: ${lineWidth} });`
  }

  // ── Expressions ────────────────────────────────────────────────────────────

  transpileExpr(expr: Expr): string {
    switch (expr.kind) {
      case 'NumberLit':
        return String(expr.value)
      case 'StringLit':
        return JSON.stringify(expr.value)
      case 'BoolLit':
        return String(expr.value)
      case 'NaLit':
        return 'null'
      case 'Identifier': {
        const name = expr.name
        // Pine built-in constants
        if (name === 'na') return 'null'
        if (name === 'bar_index') return 'bar_index'
        if (name.startsWith('color.')) return JSON.stringify('#888888')
        return jsIdent(name)
      }
      case 'SeriesRef': {
        const s = expr
        const series = this.transpileExpr(s.series)
        const offset = this.transpileExpr(s.offset)
        return `$shift(${series}, ${offset})`
      }
      case 'BinaryExpr': {
        const left = this.transpileExpr(expr.left)
        const right = this.transpileExpr(expr.right)
        const leftSeries = this.isSeries(expr.left)
        const rightSeries = this.isSeries(expr.right)
        if (leftSeries || rightSeries) {
          return this.seriesOp(left, right, expr.op)
        }
        return this.scalarOp(left, right, expr.op)
      }
      case 'UnaryExpr': {
        const u = expr
        const operand = this.transpileExpr(u.operand)
        if (u.op === '-') {
          if (this.isSeries(u.operand)) return `$neg(${operand})`
          return `(-${operand})`
        }
        if (u.op === 'not') {
          if (this.isSeries(u.operand)) return `$not(${operand})`
          return `(!${operand})`
        }
        return operand
      }
      case 'TernaryExpr': {
        const cond = this.transpileExpr(expr.condition)
        const cons = this.transpileExpr(expr.consequent)
        const alt = this.transpileExpr(expr.alternate)
        const hasSeries =
          this.isSeries(expr.condition) ||
          this.isSeries(expr.consequent) ||
          this.isSeries(expr.alternate)
        if (hasSeries) return `$ternary(${cond}, ${cons}, ${alt})`
        return `(${cond} ? ${cons} : ${alt})`
      }
      case 'CallExpr':
        return this.transpileCall(expr)
    }
  }

  private transpileCall(call: CallExpr): string {
    const name = call.callee.join('.')

    // input.*() → return default value as scalar
    if (call.callee[0] === 'input') {
      const defaultArg = call.args[0]
      return defaultArg ? this.transpileExpr(defaultArg) : '0'
    }

    // na() → null
    if (name === 'na') {
      if (call.args.length > 0) {
        // na(value) checks if value is na
        const v = this.transpileExpr(call.args[0])
        return `(${v} === null || ${v} === undefined)`
      }
      return 'null'
    }

    // nz(value, default?) → fill na
    if (name === 'nz') {
      const v = call.args[0] ? this.transpileExpr(call.args[0]) : 'null'
      const d = call.args[1] ? this.transpileExpr(call.args[1]) : '0'
      return `$fillNa(${v}, ${d})`
    }

    // math.*()
    if (call.callee[0] === 'math') {
      const fn = call.callee[1] ?? ''
      const args = call.args.map((a) => this.transpileExpr(a)).join(', ')
      return `Math.${fn}(${args})`
    }

    // ta.*()
    if (call.callee[0] === 'ta') {
      return this.transpileTaCall(call)
    }

    // array.*() → limited support
    if (call.callee[0] === 'array') {
      const fn = call.callee[1]
      if (fn === 'new') return '[]'
      if (fn === 'push') {
        const arr = this.transpileExpr(call.args[0])
        const val = call.args[1] ? this.transpileExpr(call.args[1]) : 'null'
        return `(${arr}.push(${val}), ${arr})`
      }
      return '[]'
    }

    // Regular function call (user-defined or passthrough)
    const args = call.args.map((a) => this.transpileExpr(a)).join(', ')
    const fn = jsIdent(call.callee[call.callee.length - 1])
    return `${fn}(${args})`
  }

  private transpileTaCall(call: CallExpr): string {
    const fn = call.callee[1] ?? ''
    const def = TA_BUILTINS[fn]

    if (!def) {
      this.error(`Unknown ta.${fn}() — not supported in Pine Lite`)
      return 'null'
    }

    // Special handling for functions whose arg mapping differs
    if (fn === 'atr') {
      const period = call.args[0] ? this.transpileExpr(call.args[0]) : '14'
      return `atr(high, low, close, ${period})`
    }
    if (fn === 'stdev') {
      const src = call.args[0] ? this.transpileExpr(call.args[0]) : 'close'
      const period = call.args[1] ? this.transpileExpr(call.args[1]) : '20'
      return `_stdev(${src}, ${period})`
    }
    if (fn === 'supertrend') {
      const factor = call.args[0] ? this.transpileExpr(call.args[0]) : '3'
      const period = call.args[1] ? this.transpileExpr(call.args[1]) : '10'
      return `_supertrend(${factor}, ${period})`
    }
    if (fn === 'bb') {
      const src = call.args[0] ? this.transpileExpr(call.args[0]) : 'close'
      const len = call.args[1] ? this.transpileExpr(call.args[1]) : '20'
      const mult = call.args[2] ? this.transpileExpr(call.args[2]) : '2'
      return `_bb(${src}, ${len}, ${mult})`
    }
    if (fn === 'kc') {
      const src = call.args[0] ? this.transpileExpr(call.args[0]) : 'close'
      const len = call.args[1] ? this.transpileExpr(call.args[1]) : '20'
      const mult = call.args[2] ? this.transpileExpr(call.args[2]) : '1.5'
      return `_kc(${src}, high, low, ${len}, ${mult})`
    }
    if (fn === 'vwma') {
      const src = call.args[0] ? this.transpileExpr(call.args[0]) : 'close'
      const len = call.args[1] ? this.transpileExpr(call.args[1]) : '20'
      return `_vwma(${src}, volume, ${len})`
    }
    if (fn === 'stoch') {
      const src = call.args[0] ? this.transpileExpr(call.args[0]) : 'close'
      const high_ = call.args[1] ? this.transpileExpr(call.args[1]) : 'high'
      const low_ = call.args[2] ? this.transpileExpr(call.args[2]) : 'low'
      const period = call.args[3] ? this.transpileExpr(call.args[3]) : '14'
      return `_stoch(${src}, ${high_}, ${low_}, ${period})`
    }
    if (fn === 'dmi') {
      const period = call.args[0] ? this.transpileExpr(call.args[0]) : '14'
      const smooth = call.args[1] ? this.transpileExpr(call.args[1]) : '14'
      return `_dmi(${period}, ${smooth})`
    }

    // Generic mapping
    const args = call.args.map((a) => this.transpileExpr(a)).join(', ')
    return `${def.jsName}(${args})`
  }

  // ── Series helpers ─────────────────────────────────────────────────────────

  private seriesOp(left: string, right: string, op: BinaryOp): string {
    const opFn: Record<BinaryOp, string> = {
      '+': `(a,b)=>a+b`,
      '-': `(a,b)=>a-b`,
      '*': `(a,b)=>a*b`,
      '/': `(a,b)=>b!==0?a/b:null`,
      '%': `(a,b)=>b!==0?a%b:null`,
      '^': `(a,b)=>Math.pow(a,b)`,
      '==': `(a,b)=>a===b`,
      '!=': `(a,b)=>a!==b`,
      '<': `(a,b)=>a<b`,
      '>': `(a,b)=>a>b`,
      '<=': `(a,b)=>a<=b`,
      '>=': `(a,b)=>a>=b`,
      and: `(a,b)=>a&&b`,
      or: `(a,b)=>a||b`,
    }
    return `$sop(${left}, ${right}, ${opFn[op]})`
  }

  private scalarOp(left: string, right: string, op: BinaryOp): string {
    const jsOp: Record<BinaryOp, string> = {
      '+': '+',
      '-': '-',
      '*': '*',
      '/': '/',
      '%': '%',
      '^': '**',
      '==': '===',
      '!=': '!==',
      '<': '<',
      '>': '>',
      '<=': '<=',
      '>=': '>=',
      and: '&&',
      or: '||',
    }
    return `(${left} ${jsOp[op]} ${right})`
  }

  // ── Series type inference ──────────────────────────────────────────────────

  private isSeries(expr: Expr): boolean {
    switch (expr.kind) {
      case 'NumberLit':
      case 'StringLit':
      case 'BoolLit':
      case 'NaLit':
        return false
      case 'Identifier':
        return this.seriesVars.has(expr.name) || BUILTIN_SERIES.has(expr.name)
      case 'SeriesRef':
        return true // historical ref always produces a series
      case 'BinaryExpr':
        return this.isSeries(expr.left) || this.isSeries(expr.right)
      case 'UnaryExpr':
        return this.isSeries(expr.operand)
      case 'TernaryExpr':
        return (
          this.isSeries(expr.condition) ||
          this.isSeries(expr.consequent) ||
          this.isSeries(expr.alternate)
        )
      case 'CallExpr': {
        const name = expr.callee[0]
        if (name === 'ta') return true // all ta.* return series
        if (name === 'input') return false // input.* return scalars
        return true // other calls assumed series
      }
    }
  }

  private getTupleKeys(expr: Expr): string[] {
    if (expr.kind !== 'CallExpr') return []
    const name = expr.callee[1] ?? ''
    return TA_BUILTINS[name]?.tupleKeys ?? []
  }
}

// ── Valid JS identifier ───────────────────────────────────────────────────────

function jsIdent(name: string): string {
  return name.replace(/[^a-zA-Z0-9_$]/g, '_')
}

// ── Public API ────────────────────────────────────────────────────────────────

export function transpile(program: Program): TranspileResult {
  const t = new Transpiler()
  return t.transpile(program)
}
